import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { ChannelList, useChatContext } from 'stream-chat-react'
import { debounce } from 'lodash'
import SearchIcon from 'components/Common/SvgIcons/SearchIcon'
import { MessagingChannelList, MessagingChannelPreview } from 'components/Chat/components'
import { useUnreadContext } from 'components/Chat/contexts/UnreadContext'
import { Channel } from 'stream-chat'
import { DefaultStreamChatGenerics } from 'components/Chat/types'
import { useTenantId } from 'utils/useTenantId'
import { ampli } from 'ampli'
import UserGroupIcon from 'assets/svgs/user-group.svg'
import PencilSquareIcon from 'assets/svgs/pencil-square.svg'
import { getSearchOperator } from '../../utils/searchHelpers'

export const getTotalChatUnreadCount = (channels: Channel<DefaultStreamChatGenerics>[]) => {
  return channels.map((channel) => channel.countUnread()).reduce((total, count) => total + count, 0)
}

const ChannelListHeader = ({
  userId,
  usersAssessmentResponseByQuestion,
  setShowChannelCreate,
  showChannelCreate,
  setShowChannelPreview,
  showChannelPreview,
  chatUnreadMessageCountAction,
  chatUnreadMessageCount,
  setShowUserSelection,
  showUserSelection,
  setShowGroupCreation,
  showGroupCreation,
  isSelectingChannel = false,
  setInitialJumpMessageId,
}) => {
  const tenantId = useTenantId()
  const { channel: activeChannel, setActiveChannel, client } = useChatContext()

  const { setChatsUnreadCount, channelMessageCount, setChannelMessageCount } = useUnreadContext()
  const [channelFilters]: any = useState({
    type: 'messaging',
    $and: [{ team: { $in: [tenantId] } }, { members: { $in: [userId] } }],
  })

  const [searchChannel, setSearchChannel] = useState('')
  const [inputValue, setInputValue] = useState('') // Immediate value for input display
  const [channelMatchingMessages, setChannelMatchingMessages] = useState<Map<string, any>>(
    new Map()
  )
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearchResults, setHasSearchResults] = useState(true)
  const didInitialAutoSelectRef = useRef(false)
  const stableSearchResultsRef = useRef<Map<string, any>>(new Map())

  // Function to search messages across all channels
  const searchMessages = useCallback(
    async (query: string) => {
      if (!query.trim() || !client) {
        setChannelMatchingMessages(new Map())
        stableSearchResultsRef.current = new Map()
        return
      }

      setIsSearching(true)
      try {
        // Search for messages containing the query
        // Use appropriate operator based on whether query contains special characters
        const messageFilterConditions = getSearchOperator(query)
        const searchResponse = await client.search(
          {
            type: 'messaging',
            $and: [{ team: { $in: [tenantId] } }, { members: { $in: [userId] } }],
          },
          messageFilterConditions,
          {
            limit: 50,
          }
        )

        const results = searchResponse.results || []

        // Create a map of channel IDs to their matching messages
        const channelMessageMap = new Map()
        results.forEach((result) => {
          if (result.message && result.message.cid) {
            const channelId = result.message.cid
            if (!channelMessageMap.has(channelId)) {
              channelMessageMap.set(channelId, [])
            }
            channelMessageMap.get(channelId).push(result.message)
          }
        })

        // Sort messages within each channel by latest message (newest first)
        channelMessageMap.forEach((messages, channelId) => {
          messages.sort((a, b) => {
            const timeA = new Date(a.created_at || 0).getTime()
            const timeB = new Date(b.created_at || 0).getTime()
            return timeB - timeA // Descending order (newest first)
          })
        })

        setChannelMatchingMessages(channelMessageMap)
        // Store stable search results in ref to prevent disappearing during state transitions
        stableSearchResultsRef.current = channelMessageMap
      } catch (error) {
        console.error('Error searching messages:', error)
        setChannelMatchingMessages(new Map())
        stableSearchResultsRef.current = new Map()
      } finally {
        setIsSearching(false)
      }
    },
    [client, tenantId, userId]
  )

  // Debounced function for both state update and search
  const debouncedHandleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchChannel(value)
        if (value.trim()) {
          searchMessages(value)
        } else {
          setChannelMatchingMessages(new Map())
          stableSearchResultsRef.current = new Map()
        }
      }, 300),
    [searchMessages]
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel()
    }
  }, [debouncedHandleSearch])

  const customRenderFilter = (channels) => {
    try {
      setChatsUnreadCount(getTotalChatUnreadCount(channels))

      channels.forEach((channel) => (channelMessageCount[channel.cid] = channel.countUnread()))
      setChannelMessageCount(channelMessageCount)

      const searchActive = Boolean(searchChannel.trim())

      const filteredChannels = searchActive
        ? channels
            .filter((channel) => {
              // Use channelMatchingMessages if available, otherwise fall back to stable ref
              const searchResults =
                channelMatchingMessages.size > 0
                  ? channelMatchingMessages
                  : stableSearchResultsRef.current

              if (searchResults.size > 0) {
                return searchResults.has(channel.cid)
              }

              // Fallback to user name matching if no message search results
              const members = Object.values(channel?.state?.members || {})
              const query = searchChannel.toLowerCase()

              return members.some(({ user }: any) => {
                if (!user) return false
                const fullName = `${user.firstName || ''}${user.lastName || ''}`.toLowerCase()
                const displayName = (user.name || '').toLowerCase()
                const businessName = (user.businessName || '').toLowerCase()

                return (
                  (fullName && fullName.includes(query)) ||
                  (displayName && displayName.includes(query)) ||
                  (businessName && businessName.includes(query))
                )
              })
            })
            .sort((a, b) => {
              // Sort by latest matching message timestamp
              const searchResults =
                channelMatchingMessages.size > 0
                  ? channelMatchingMessages
                  : stableSearchResultsRef.current

              if (searchResults.size > 0) {
                const messagesA = searchResults.get(a.cid) || []
                const messagesB = searchResults.get(b.cid) || []

                if (messagesA.length > 0 && messagesB.length > 0) {
                  // Get the latest message from each channel (already sorted by newest first)
                  const latestA = new Date(messagesA[0].created_at || 0).getTime()
                  const latestB = new Date(messagesB[0].created_at || 0).getTime()
                  return latestB - latestA // Descending order (newest first)
                }
                if (messagesA.length > 0) return -1
                if (messagesB.length > 0) return 1
              }

              // Fallback: sort by channel's last message
              const lastMessageA = a.state.messages?.[a.state.messages.length - 1]
              const lastMessageB = b.state.messages?.[b.state.messages.length - 1]
              const timeA = lastMessageA ? new Date(lastMessageA.created_at || 0).getTime() : 0
              const timeB = lastMessageB ? new Date(lastMessageB.created_at || 0).getTime() : 0
              return timeB - timeA
            })
        : channels.slice()

      const nextHasResults = searchActive ? filteredChannels.length > 0 : true
      setHasSearchResults((prev) => (prev === nextHasResults ? prev : nextHasResults))

      // One-time initial auto-select on page load if no active channel and not in special modes
      if (
        !didInitialAutoSelectRef.current &&
        filteredChannels.length > 0 &&
        !activeChannel &&
        !showChannelCreate &&
        !showUserSelection &&
        !showGroupCreation &&
        !isSelectingChannel
      ) {
        didInitialAutoSelectRef.current = true
        setTimeout(() => {
          if (
            !activeChannel &&
            !showChannelCreate &&
            !showUserSelection &&
            !showGroupCreation &&
            !isSelectingChannel
          ) {
            setActiveChannel(filteredChannels[0])
            setShowChannelPreview(true)
          }
        }, 50)
      }

      // Ensure chat preview is shown when we have channels (unless creating new channel)
      if (filteredChannels.length > 0 && !showChannelCreate && !showChannelPreview) {
        setShowChannelPreview(true)
      }

      return filteredChannels
    } catch (err) {
      return channels
    }
  }

  const searchQueryHandler = (e) => {
    // Search is now handled by the useEffect with debouncing
    // This function can be removed or used for additional Enter key handling if needed
  }

  const handleNewMessage = () => {
    setShowUserSelection?.(true)
    setShowChannelCreate?.(false)
    setShowGroupCreation?.(false)
    ampli.newMessageInitiated()
  }

  const handleCreateGroup = () => {
    setShowGroupCreation?.(true)
    setShowChannelCreate?.(false)
    setShowUserSelection?.(false)
  }

  // Show user selection or group creation UI
  if (showUserSelection) {
    return null // Let parent handle these views
  }

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-app-grey-25 h-[70px]">
        <h1 className="text-xl font-semibold text-app-black-100 mb-0">Conversations</h1>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-3 border-b border-app-grey-25">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-app-grey-55" />
          <input
            type="text"
            placeholder="Search messages..."
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value
              setInputValue(value) // Update input immediately for responsive UI
              debouncedHandleSearch(value) // Debounce state update and search
            }}
            onKeyPress={searchQueryHandler}
            className="w-full pl-12 pr-4 py-2.5 bg-app-grey-20 border border-app-grey-25 rounded-full text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
          />
          {isSearching && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-app-primary-60"></div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="py-3 border-b border-app-grey-25">
        <button
          onClick={handleNewMessage}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left text-app-black-100 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <span className="font-medium text-sm">New message</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <img src={PencilSquareIcon} alt="new-message" className="w-5 h-5" />
          </div>
        </button>

        <div className="my-1.5 h-px bg-app-grey-25" />

        <button
          onClick={handleCreateGroup}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left text-app-black-100 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <span className="font-medium text-sm">Create a group</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <img src={UserGroupIcon} alt="create-group" className="w-5 h-5" />
          </div>
        </button>
      </div>

      {/* Channel List */}
      <div className="flex-1 overflow-hidden">
        <ChannelList
          channelRenderFilterFn={customRenderFilter}
          filters={channelFilters}
          List={(props) => (
            <MessagingChannelList
              {...props}
              isSearching={isSearching}
              hasSearchResults={hasSearchResults}
              searchQuery={searchChannel}
            />
          )}
          setActiveChannelOnMount={false}
          Preview={(props) => (
            <MessagingChannelPreview
              {...(props as any)}
              searchChannel={searchChannel}
              showChannelCreate={showChannelCreate}
              setShowChannelCreate={setShowChannelCreate}
              setShowChannelPreview={setShowChannelPreview}
              usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
              chatUnreadMessageCountAction={chatUnreadMessageCountAction}
              channelMatchingMessages={channelMatchingMessages}
              isSearching={isSearching}
              setInitialJumpMessageId={setInitialJumpMessageId}
            />
          )}
        />
      </div>
    </div>
  )
}

export default ChannelListHeader
